import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiDotsHorizontal } from '@mdi/js';
import PopupTitle from '../common/popup_title';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import RecipeBoardsPickerNewBoardPage from './recipe_boards_picker_new_board_page';
import { useRecipesBoardId } from './recipes_hooks';
import { useIsMobile } from '../common/layout_hooks';
import {
  useCreateSmorgBoardMutation,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { healthProGroupForSpaceID, userIsCreator } from '../../services/users';
import {
  CURRENT_SMORG_BOARD_SCHEMA_VERSION,
  defaultMenuStructureForAdditionalBoard,
  isBoardPublishedToSpace,
} from '../../services/smorg_board';
import { useTracker } from '../../operations/tracking_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { ActivityObjectType } from '../../API';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import DeleteLinkedMealNotAllowedPopover from '../../features/smorg_studio_programmes/components/delete_linked_meal_not_allowed_popover';
import { sendNotificationToAllDevicesOperation } from '../../operations/push_notification_operations';
import PublishConfirmationNotificationModal from '../creator_studio/publish_confirmation_notification_modal';

const RecipeBoardActionsPopupMenu = ({
  sharedProgrammesLinked,
  onEditRecipesBoard,
  onDuplicateRecipesBoard,
  onShareRecipesBoard,
  onDeleteRecipesBoard,
  editBoardDisabled,
  duplicateBoardDisabled,
  shareBoardDisabled,
  deleteBoardDisabled,
  onPublishRecipesBoardToSpace,
  onUnpublishRecipesBoardFromSpace,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = !!anchorEl;
  const [deletePopupAnchorEl, setDeletePopupAnchorEl] = useState(null);

  const [
    publishConfirmationNotificationVisible,
    setPublishConfirmationNotificationVisible,
  ] = useState(false);

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const selectedRecipesBoardId = useRecipesBoardId();

  const selectedRecipesBoardTitle = (ownedRecipesBoards || []).find(
    (b) => b.id === selectedRecipesBoardId,
  )?.title;

  const sortedOwnRecipesBoards = useMemo(() => {
    return [...(ownedRecipesBoards || [])].sort((a, b) =>
      a.updatedAt < b.updatedAt ? 1 : -1,
    );
  }, [ownedRecipesBoards]);

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const tracker = useTracker();

  const [createSmorgBoard] = useCreateSmorgBoardMutation();

  const { data: space } = useGetSpaceQuery(undefined, { skip: !isSmorgStudio });

  const currentHealthProGroup = space && healthProGroupForSpaceID(space.id);

  const [showNewBoardPage, setShowNewBoardPage] = useState(false);
  const [creationInProgress, setCreationInProgress] = useState(false);
  const navigate = useNavigate();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setShowNewBoardPage(false);
    setAnchorEl(null);
  };

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const handleCreateNewRecipesBoard = async (title) => {
    setShowNewBoardPage(false);
    handleClose();
    setCreationInProgress(true);
    const input = {
      title,
      schemaVersion: CURRENT_SMORG_BOARD_SCHEMA_VERSION,
      spaceMembershipID: currentSpaceMembership?.id,
      groups: currentHealthProGroup && [currentHealthProGroup],
      // Must create a default menu, otherwise it's not obvious
      // for the user what to do with the board just created.
      menus: defaultMenuStructureForAdditionalBoard(),
    };
    const { data: recipesBoard } = await createSmorgBoard(input);
    tracker(
      userProfile,
      ['Recipes board created'],
      ['numRecipesBoardsCreated'],
    );
    setCreationInProgress(false);
    const newRecipesBoardId = recipesBoard?.id;
    if (newRecipesBoardId) {
      navigate(`/recipes/${newRecipesBoardId}`);
    }
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: recipesBoard.id,
      });
    }
  };

  const handleEditRecipesBoard = () => {
    handleClose();
    onEditRecipesBoard();
  };

  const handleDuplicateRecipesBoard = () => {
    handleClose();
    onDuplicateRecipesBoard();
  };

  const handleShareRecipesBoard = () => {
    handleClose();
    onShareRecipesBoard();
  };

  const handleDeleteRecipesBoard = () => {
    setDeletePopupAnchorEl(null);
    handleClose();
    onDeleteRecipesBoard();
  };

  const onSelectBoard = (recipesBoardId) => {
    handleClose();
    tracker(userProfile, ['Switch to recipes board']);
    navigate(`/recipes/${recipesBoardId}`);
  };

  const { data: selectedRecipesBoard } = useGetSmorgBoardQuery(
    selectedRecipesBoardId,
    {
      skip: !selectedRecipesBoardId,
    },
  );

  const isPublishedToSpace = isBoardPublishedToSpace(
    selectedRecipesBoard,
    space,
  );

  const boardEmbeddedInProgrammeID = false;
  const isBoardEmbeddedInProgramme = !!boardEmbeddedInProgrammeID;

  const handlePublishRecipesBoardToSpace = () => {
    handleClose();
    setPublishConfirmationNotificationVisible(true);
  };

  const handleUnpublishRecipesBoardFromSpace = () => {
    handleClose();
    onUnpublishRecipesBoardFromSpace();
  };

  const onPublishAndNotify = async (
    shouldSendPushNotification,
    notificationTitle,
    notificationText,
  ) => {
    setPublishConfirmationNotificationVisible(false);
    onPublishRecipesBoardToSpace();
    if (shouldSendPushNotification) {
      console.log('Sending push notification');
      sendNotificationToAllDevicesOperation(
        notificationTitle,
        notificationText,
      );
    }
  };

  const isMobile = useIsMobile();

  return (
    <>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        PaperProps={{
          style: { minWidth: '250px' },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}>
        {showNewBoardPage ? (
          <RecipeBoardsPickerNewBoardPage
            handleClose={handleClose}
            handleClickBack={() => setShowNewBoardPage(false)}
            onCreateNewBoard={handleCreateNewRecipesBoard}
            creationInProgress={creationInProgress}
            defaultTitle="My recipes"
          />
        ) : (
          <>
            <PopupTitle
              titleText={selectedRecipesBoardTitle || 'Choose option'}
              closeButtonEnabled
              onClickClose={handleClose}
            />
            <MenuItem onClick={() => setShowNewBoardPage(true)}>
              Create new recipe board
            </MenuItem>
            <MenuItem
              onClick={handleEditRecipesBoard}
              disabled={editBoardDisabled}>
              Edit recipe board
            </MenuItem>
            <MenuItem
              onClick={handleDuplicateRecipesBoard}
              disabled={duplicateBoardDisabled}>
              Duplicate board
            </MenuItem>
            <MenuItem
              onClick={handleShareRecipesBoard}
              disabled={shareBoardDisabled}>
              Share board
            </MenuItem>
            {isSmorgStudio &&
              !isMobile &&
              !isBoardEmbeddedInProgramme &&
              !false /* isProductizedSharedBoard */ && (
                <MenuItem onClick={handlePublishRecipesBoardToSpace}>
                  {isPublishedToSpace
                    ? 'Publish changes to space'
                    : 'Publish board to space'}
                </MenuItem>
              )}
            {isSmorgStudio &&
              !isMobile &&
              !isBoardEmbeddedInProgramme &&
              !false /* isProductizedSharedBoard */ &&
              isPublishedToSpace && (
                <MenuItem onClick={handleUnpublishRecipesBoardFromSpace}>
                  Unpublish board from space
                </MenuItem>
              )}
            <MenuItem
              onClick={(ev) => setDeletePopupAnchorEl(ev.target)}
              disabled={deleteBoardDisabled}>
              Delete board
            </MenuItem>
            <MenuItem
              sx={{ fontWeight: 'bold', opacity: '1 !important' }}
              disabled>
              Switch to
            </MenuItem>
            {(sortedOwnRecipesBoards || []).map((recipesBoard) => (
              <MenuItem
                disabled={recipesBoard.id === selectedRecipesBoardId}
                key={recipesBoard.id}
                onClick={() => {
                  onSelectBoard(recipesBoard.id);
                }}>
                {recipesBoard.title}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls="long-menu"
        aria-expanded={open && 'true'}
        aria-haspopup="true"
        size="small"
        className="actionsPopupMenu"
        onClick={handleClick}>
        <Icon path={mdiDotsHorizontal} />
      </IconButton>
      <DeleteCardConfirmPopover
        anchorEl={deletePopupAnchorEl}
        visible={
          !!deletePopupAnchorEl &&
          (!sharedProgrammesLinked || sharedProgrammesLinked.length === 0)
        }
        onDismiss={() => setDeletePopupAnchorEl(null)}
        message="The board and all meals will be deleted permanently. Are you sure?"
        onConfirmDelete={handleDeleteRecipesBoard}
        height={160}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        title="Delete all meals"
      />
      <DeleteLinkedMealNotAllowedPopover
        visible={
          !!deletePopupAnchorEl &&
          sharedProgrammesLinked &&
          sharedProgrammesLinked.length > 0
        }
        sharedProgrammesLinked={sharedProgrammesLinked}
        anchorEl={deletePopupAnchorEl}
        onDismiss={() => setDeletePopupAnchorEl(null)}
      />
      {publishConfirmationNotificationVisible && (
        <PublishConfirmationNotificationModal
          titleText="Publish your recipes board"
          promptText="Are you sure you want to publish your recipes board to your customers?"
          notificationTitleTemplate="New collection available"
          notificationTextTemplate="Check out the latest collection"
          onPublish={onPublishAndNotify}
          onDismiss={() => setPublishConfirmationNotificationVisible(false)}
        />
      )}
    </>
  );
};

RecipeBoardActionsPopupMenu.propTypes = {
  sharedProgrammesLinked: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
  ),
  onEditRecipesBoard: PropTypes.func.isRequired,
  onDuplicateRecipesBoard: PropTypes.func.isRequired,
  onShareRecipesBoard: PropTypes.func.isRequired,
  onDeleteRecipesBoard: PropTypes.func.isRequired,
  editBoardDisabled: PropTypes.bool.isRequired,
  duplicateBoardDisabled: PropTypes.bool.isRequired,
  shareBoardDisabled: PropTypes.bool.isRequired,
  deleteBoardDisabled: PropTypes.bool.isRequired,
  onPublishRecipesBoardToSpace: PropTypes.func.isRequired,
  onUnpublishRecipesBoardFromSpace: PropTypes.func.isRequired,
};

RecipeBoardActionsPopupMenu.defaultProps = {
  sharedProgrammesLinked: [],
};

export default RecipeBoardActionsPopupMenu;
